import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useParams} from 'react-router-dom/cjs/react-router-dom.min'
import {HTTP_STATUS_OK, SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {useAuth} from '../../authentication/AuthProvider'
import {getClientId} from '../../authentication/AuthUtils'
import {RoleAssignForm} from '../../healthcare/RoleAssignForm'
import {actionTableKeys} from '../../new-table/Constants'
import {severityNotification} from '../../notification/NotificationConstant'
import {showNotification} from '../../notification/NotificationService'
import {
    usePageUserRoles,
    useUserRoleCRUD,
} from '../../role/query-keys/UserRoleManager'
import {permissionOptionKeys} from '../../role/RoleConstant'
import {useRoleContext} from '../../role/RoleProvider'
import {getPageUserRoles} from '../../role/UserRoleEndPoint'
import {useImportNotificationContext} from '../../table-utils/importForm/ImportNotificationProvider'
import {
    filterDataTableEvent,
    filterEventTypes,
    getFilterEventKey,
    removeFilterChangeListener,
} from '../broadcast/FilterEvent'
import {
    exportColumnTypes,
    exportRecordTypes,
    exportTypes,
    tableToolbarType,
} from '../Constant'
import {DatatableContext} from '../DataTableContext'
import StyledDataTableWrapper from '../StyledDataTableWrapper'
import {
    getAdvancedFilter,
    getShouldRememberAdvancedFilter,
    tableTypes,
} from '../StyledTableUtils'
import {
    getHealthCareEventKey,
    healthCareEvent,
    healthCareEventTypes,
    onHealthCareRoleChangeRemoveListener,
} from './broadcast/HealthCarePractitionerEvent'
import {hcpRoleColumnList} from './HealthCarePractitionerConstants'

const StyledHealthCarePractitionerRoleDatatable = () => {
    useSignals()

    const queryClient = useQueryClient()
    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        if (!idString) {
            return null
        }
        return parseInt(idString)
    }, [idString])
    //#endregion

    //#region Context
    const {user} = useAuth()
    const {userRole, hospitalsBelong} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {hcpManagementPermissions, supportTeamManagementPermissions} =
        useRoleContext()
    const {handleOnExportDatatable} = useImportNotificationContext()
    //#endregion

    //#region Load Data from Local Storage
    const initialAdvancedFilterRequest = useMemo(() => {
        const didRememberSettings = getShouldRememberAdvancedFilter(
            tableTypes.hcpRole,
        )

        if (didRememberSettings) {
            const storedFilterRequest = getAdvancedFilter(tableTypes.hcpRole)
            if (
                !!storedFilterRequest &&
                Object.keys(storedFilterRequest).length > 0
            ) {
                return storedFilterRequest
            }
        }

        return {}
    }, [])
    //#endregion

    //#region State Controls
    const pageable = useSignal({
        pageRows: 10,
        pageNumber: 0,
        sortField: 'createdAt',
        sortOrder: -1,
    })

    const filterRequest = useSignal({
        globalSearchValue: null,
        userId: id,
        ...initialAdvancedFilterRequest,
    })

    // Pageable
    const handleChangeSortColumn = useCallback(
        e => {
            pageable.value = {
                ...pageable.value,
                sortField: e.sortField,
                sortOrder: e.sortOrder === 1 ? -1 : 1,
            }
        },
        [pageable.value.sortField, pageable.value.sortOrder],
    )

    const handleChangePageNumber = useCallback(
        newPageNumber => {
            pageable.value = {
                ...pageable.value,
                pageNumber: newPageNumber,
            }

            handlePrefetchDataByPage(newPageNumber + 1)
        },
        [pageable.value.pageNumber],
    )

    const handleChangePageRows = useCallback(
        newPageRows => {
            pageable.value = {
                ...pageable.value,
                pageRows: newPageRows,
                pageNumber: 0,
            }

            pagePrefetchRef.current = [0]

            handlePrefetchDataByPage(1)
        },
        [pageable.value.pageRows],
    )

    // Global Search Value
    const handleChangeGlobalSearch = useCallback(value => {
        filterRequest.value = {
            ...filterRequest.value,
            globalSearchValue: value || null,
        }
    }, [])

    // Filter Request
    const handleClearFilterRequest = () => {
        filterRequest.value = {
            globalSearchValue: null,
            userId: id,
        }
    }

    const handleClearFilterRequestByKey = useCallback((key, value) => {
        filterRequest.value = {
            ...filterRequest.value,
            [key]: value,
        }
    }, [])

    const handleClearAdvancedFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: filterRequest.value?.globalSearchValue || null,
            userId: id,
        }
    }, [])

    // Table Action CRUD
    const formRef = useRef(null)

    const {mutateAsync: deleteUserRole} = useUserRoleCRUD(
        QueryCRUDTypes.delete,
        user?.id,
    )

    const handleAddNew = useCallback(() => {
        formRef.current?.showForm()
    }, [])

    const handleOnDelete = async data => {
        if (!!data && !!data?.id) {
            const response = await deleteUserRole(data)
            if (response === HTTP_STATUS_OK) {
                const {userRoleKeys} = QueryKeyManager(
                    QueryKeyTypes.userRole,
                    user?.id,
                )

                //TODO: Temporary Refetch Table
                queryClient.invalidateQueries(userRoleKeys.pattern())
            }
        }
    }

    const handleAction = (type, rowValue) => {
        if (type === actionTableKeys.delete && rowValue?.id) {
            if (
                hospitalsBelong?.length &&
                hospitalsBelong?.some(hos => hos.id === rowValue?.hospitalId)
            ) {
                handleOnDelete(rowValue)
            } else {
                showNotification(
                    severityNotification.warn,
                    'You do not have the necessary permissions in this hospital for this privilege.',
                )
            }
        }
    }
    //#endregion

    //#region Check Permission
    const handleCheckPermissionByKey = useCallback(
        key => {
            if (!key) {
                return false
            }

            if (userRole === SystemRole.ADMIN) {
                return true
            }

            if (!hcpManagementPermissions?.length) {
                return false
            }

            if (key === permissionOptionKeys.allowToDelete) {
                return hcpManagementPermissions?.includes(
                    permissionOptionKeys.allowToDeleteHcpRole,
                )
            } else {
                return hcpManagementPermissions?.includes(key)
            }
        },
        [userRole, hcpManagementPermissions],
    )
    //#endregion

    //#region Data Controls
    const getCurrentQueryKey = ({newPageNumber, newPageRows}) => {
        const {userRoleKeys} = QueryKeyManager(QueryKeyTypes.userRole, user?.id)

        return userRoleKeys.page({
            pageNumber:
                newPageNumber !== null && typeof newPageNumber === 'number'
                    ? newPageNumber
                    : pageable.value.pageNumber,
            pageRows:
                newPageRows !== null && typeof newPageNumber === 'number'
                    ? newPageRows
                    : pageable.value.pageRows,
            sortField: pageable.value.sortField,
            sortOrder: pageable.value.sortOrder,
            filterRequest: {
                ...filterRequest.value,
            },
        })
    }

    const {data: queryData, isFetched} = usePageUserRoles(
        user?.id,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        filterRequest.value,
        true,
    )

    const dataList = useRef([])
    const currentDataList = useMemo(() => {
        dataList.current = queryData?.content || []

        return queryData?.content || []
    }, [queryData?.content])

    // Page Rows from Pagination
    const rowsOptionHoveredRef = useRef([10])
    const handlePrefetchDataByRowsOption = useCallback(rows => {
        if (!!user?.id && !rowsOptionHoveredRef.current?.includes(rows)) {
            rowsOptionHoveredRef.current.push(rows)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: 0,
                    newPageRows: rows,
                }),
                queryFn: () =>
                    getPageUserRoles(
                        user?.id,
                        0,
                        rows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    const handleOnDetailRowClick = id => {
        if (!!id) {
            const selectedRowData = dataList.current?.find(row => row.id === id)
            if (!!selectedRowData) {
                if (
                    hospitalsBelong?.length &&
                    hospitalsBelong.some(
                        hos => hos?.id === selectedRowData?.hospitalId,
                    )
                ) {
                    formRef.current?.showForm(selectedRowData)
                } else {
                    showNotification(
                        severityNotification.warn,
                        'You do not have the necessary permissions in this hospital for this privilege.',
                    )
                }
            }
        }
    }

    // Page Number from Pagination
    const pagePrefetchRef = useRef([0])
    const handlePrefetchDataByPage = useCallback(pageNumber => {
        if (!!user?.id && !pagePrefetchRef.current?.includes(pageNumber)) {
            pagePrefetchRef.current.push(pageNumber)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: pageNumber,
                    newPageRows: pageable.peek().pageRows,
                }),
                queryFn: () =>
                    getPageUserRoles(
                        user?.id,
                        pageNumber,
                        pageable.peek().pageRows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    //#region Event Listener on State Changes
    useEffect(() => {
        // TABLE
        healthCareEvent.on(
            getHealthCareEventKey(healthCareEventTypes.ROLE_CHANGE),
            data => {
                if (
                    !data ||
                    !data?.id ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                const {userRoleKeys} = QueryKeyManager(
                    QueryKeyTypes.userRole,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: userRoleKeys.pattern(),
                })
            },
        )

        handlePrefetchDataByPage(pageable.value.pageNumber + 1)

        filterDataTableEvent.on(
            `${getFilterEventKey(
                tableTypes.hcpRole,
                filterEventTypes.advanced,
            )}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearAdvancedFilterRequest()

                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        return () => {
            onHealthCareRoleChangeRemoveListener()
            removeFilterChangeListener(
                tableTypes.hcpRole,
                filterEventTypes.advanced,
            )
        }
    }, [])
    //#endregion

    //#region Export Data
    const handleRetrieveExportData = async (
        {
            exportType = exportTypes.excel, // csv, excel, pdf
            exportRecordType = exportRecordTypes.allRecords, // all, currentInView
            exportColumns = exportColumnTypes.allColumns, // all, currentInView
        },
        columns,
    ) => {
        handleOnExportDatatable(
            {
                exportType: exportType,
                exportRecordType: exportRecordType,
                exportColumns: exportColumns,
            },
            queryData,
            pageable.value,
            filterRequest.value,
            columns,
            tableTypes.hcpRole,
        )
    }
    //#endregion

    return (
        <DatatableContext.Provider
            value={{
                dataKey: 'id',
                tableType: tableTypes.hcpRole,
                queryData: queryData,
                isFetched: isFetched,
                columnList: hcpRoleColumnList,
                tableToolbar: [tableToolbarType.tableControlToolbar],
                tableActions: [actionTableKeys.delete],

                pageable: pageable.value,
                onChangeSortColumn: handleChangeSortColumn,
                onChangePageNumber: handleChangePageNumber,
                onChangePageRows: handleChangePageRows,
                onPageRowsHovered: handlePrefetchDataByRowsOption,

                onDetailRowClick: handleOnDetailRowClick,
                handleCheckPermissionByKey: handleCheckPermissionByKey,

                initialAdvancedFilterRequest: initialAdvancedFilterRequest,
                handleClearFilterRequest: handleClearFilterRequest,
                handleClearFilterRequestByKey: handleClearFilterRequestByKey,
                handleClearAdvancedFilterRequest:
                    handleClearAdvancedFilterRequest,

                handleRetrieveExportData: handleRetrieveExportData,

                handleChangeGlobalSearch: handleChangeGlobalSearch,

                handleAddNew: handleAddNew,
                handleAction: handleAction,
            }}>
            <StyledDataTableWrapper
                headerOptions={{
                    title: "HCP's Roles Management",
                    subTitle:
                        'Manage roles in hospitals where health care professionals are assigned to participate in medical care and treatment.',
                }}
            />
            <RoleAssignForm ref={formRef} />
        </DatatableContext.Provider>
    )
}

export default StyledHealthCarePractitionerRoleDatatable
